<template>
  <v-simple-table>
    <thead>
      <th
        v-for="(header, index) in filteredTeamHeaders"
        class="text-center dyna-team-header"
        :key="index"
        :class="teamHeaderClass(header)"
        @mouseenter="pushActiveTools(header.value)"
        @mouseleave="removeActiveTools(header.value)"
        @click="sortColumn(header.value)"
      >
        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <span v-bind="attrs" v-on="on">
              <span class="tooltip-display-text"> {{ header.text }}</span>
              <v-icon small :class="sortIconClass(header)">
                {{ sortIcon }}
              </v-icon>
            </span>
          </template>
          {{ header.text }}
        </v-tooltip>
      </th>
    </thead>
    <dyna-label-no-data v-if="sortedItems.length < 1" ></dyna-label-no-data>
    <tbody v-else>
      <tr v-for="(team, index) in sortedItems" :key="index">
        <template v-if="!team.loaded">
          <td :colspan="numColumns" class="text-center">
            <v-skeleton-loader
              class="insight-data-tile"
              type="image"
              max-height="10"
            ></v-skeleton-loader>
          </td>
        </template>
        <template v-else>
          <td class="text-right" style="padding-left:3rem">
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  :disabled="teamDisabled(team)"
                  @click="loadDynalabelTeam(team.team_id)"
                  text
                  small
                  class="agent-btn"
                  color="primary"
                >
                  {{ team.team_name }}
                </v-btn>
              </template>
              <span
                >{{ team.team_name }} ({{ team.team_id }}) team&hellip;</span
              >
            </v-tooltip>
          </td>
          <td class="text-center">
            <span v-if="team.call_count > 0">{{
              team.average_handle_time
            }}</span>
          </td>
          <td class="text-center">
            <span v-if="team.call_count > 0">{{
              team.average_call_volume
            }}</span>
          </td>

          <td class="text-center">
            <span v-if="team.call_count > 0">{{ team.call_count }}</span>
          </td>
          <td class="text-center">
            <score-chip
              v-if="team.call_count > 0"
              :score="team.average_call_score"
              :calls_modified="team.calls_modified"
              :aggregate_cat="false"
            ></score-chip>
            <empty-score-chip v-else></empty-score-chip>
          </td>

          <td
            v-for="(slot, i) in teamGroupedColumnSlots[team.team_id]"
            :key="slot.id"
            class="text-center"
          >
            <scorecards-category-display v-if="team.call_count > 0 && team.team_id == slot.item_id" 
              :key="i"
              :infoItem="team" 
              :infoSlot="slot"
              :nonAppCatHandling="nonAppCatHandling"
            ></scorecards-category-display>
            <empty-score-chip v-else></empty-score-chip>
          </td>
        </template>
      </tr>
    </tbody>
  </v-simple-table>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import ScoreChip from "@/components/Scorecards/ScoreChip.vue";
import moment from "moment";
import presentation from "@/mixins/presentation";
import sort from "@/mixins/sort";
import ExpandDynaLabelRow from "./ExpandDynaLabelRow.vue";
import EmptyScoreChip from "../EmptyScoreChip.vue";
import SubjectiveDistributionWidget from '@/components/Widgets/SubjectiveDistributionWidget.vue';
import ScorecardsCategoryDisplay from './ScorecardsCategoryDisplay.vue';
import DynaLabelNoData from "./DynaLabelNoData.vue";

const DATA_COLUMN_LENGTH = 5;

export default {
  components: { ScoreChip, ExpandDynaLabelRow, EmptyScoreChip, SubjectiveDistributionWidget, ScorecardsCategoryDisplay, DynaLabelNoData },
  mixins: [presentation, sort],
  props: {
    dynalabelItem: {
      type: Object,
      required: true
    },
    nonAppCatHandling: {
      type: Number,
      required: true
    },
    durationValHeaderText: {
      type: String,
      required: true
    },
    aggregateRow: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    isDesc: null,
    tooltipActive: false,
    myTooltipsActive: [],
    myTooltipsSorted: [],
    isHovering: false
  }),
  computed: {
    ...mapGetters("dateRange", ["start_date", "end_date"]),
    ...mapGetters("scorecardsV2", [
      "categorySkeletonData",
      "categorySkeletonDataLoadingStatus",
      "categorySkeletonDataLoadingError",
      "categorySkeletonDataLoadingErrorStatus"
    ]),
    ...mapGetters("scorecardsV2Aggregates", [
      "displayFormats",
      "dynaRequestCount",

      "dynaTeamRowsLoaded"
    ]),
    ...mapGetters("presentation", [
      "presentationScorecardsData",
      "presentationScorecardsDataLoadingStatus",
      "presentationScorecardsDataLoadingError",
      "presentationScorecardsDataLoadingErrorStatus"
    ]),
    ...mapGetters("resources", [
      "teamByUserData",
      "teamByUserDataLoadingStatus",
      "teamByUserDataLoadingError",
      "teamByUserDataLoadingErrorStatus"
    ]),

    sortIcon() {
      switch (this.isDesc) {
        case 2:
          return "mdi-arrow-down";
        default:
          return "mdi-arrow-up";
      }
    },
    filteredTeamHeaders() {
      // check against presentation service source keys
      var sourceKeys = this.presentationScorecardsData.map(p => p.source_key);
      return this.teamHeaders
        .filter(function(o) {
          return sourceKeys.includes(o.key);
        })
        .sort(
          ({ key: r }, { key: t }) =>
            sourceKeys.indexOf(r) - sourceKeys.indexOf(t)
        );
    },
    teamGroupedColumnSlots() {
      let slotObj = [];

      this.dynalabelItem.teams.forEach((team, teamIndex) => {
        if (
          team.hasOwnProperty("metrics") &&
          team.metrics.hasOwnProperty("categories")
        ) {
          team.metrics.categories.forEach(function(category) {
            // // if the slot object does not have an index for the dynamic label id, set it and push the category onto the index
            if (!slotObj[team.team_id]) slotObj[team.team_id] = [];

            //push category
            slotObj[team.team_id].push({
              item_id: team.team_id,
              id: category.id,
              calls_modified: category.calls_modified,
              num_not_applicable: category.num_not_applicable,
              total_calls: category.total_calls
            });
          });
        }
      });

      return slotObj;
    },
    groupedRowData() {
      let rowDataNew = [];

      this.dynalabelItem.teams.forEach((team, index) => {
        if (!team.loaded) {
          rowDataNew.push({
            name: null,
            average_handle_time: null,
            average_duration: null,
            average_call_score: null,
            average_call_volume: null,
            call_count: null,
            calls_modified: null,
            excluded_calls: null,
            loading: true,
            team_name: team.team_name,
            team_id: -index,
            id: -index
          });
        } else {
          let rowItem = {};
          if (team.hasOwnProperty("metrics")) {
            rowItem = this.scorecardsPresentationFormattedData(
              team,
              team.metrics.categories,
              true,
              false
            );
          } else {
            rowItem = this.scorecardsPresentationFormattedData(
              team,
              [],
              true,
              false
            );
          }
          rowItem.team_name = team.team_name;
          rowItem.loaded = true;
          rowItem.team_id = team.team_id;
          rowDataNew.push(rowItem);
        }
      });

      return [...rowDataNew].sort((a, b) => {
        if (a.team_name === null && b.team_name === null) return 0;
        if (a.team_name === null) return 1;
        if (b.team_name === null) return -1;
        return new Intl.Collator("en").compare(a.team_name, b.team_name);
      });
    },
    sortedRowData() {
      let rowDataNew = [];

      this.dynalabelItem.teams.forEach((team, index) => {
        if (!team.loaded) {
          rowDataNew.push({
            name: null,
            average_handle_time: null,
            average_duration: null,
            average_call_score: null,
            average_call_volume: null,
            call_count: null,
            calls_modified: null,
            excluded_calls: null,
            loaded: false,
            team_name: team.team_name,
            id: -index
          });
        } else {
          let rowItem = {};
          if (team.hasOwnProperty("metrics")) {
            rowItem = this.scorecardsPresentationFormattedData(
              team,
              team.metrics.categories,
              true,
              false
            );
          } else {
            rowItem = this.scorecardsPresentationFormattedData(
              team,
              [],
              true,
              false
            );
          }
          rowItem.team_name = team.team_name;
          rowItem.loaded = true;
          rowItem.team_id = team.team_id;
          rowDataNew.push(rowItem);
        }
      });

      return [...rowDataNew].sort((a, b) => {
        if (a.team_name === null && b.team_name === null) return 0;
        if (a.team_name === null) return 1;
        if (b.team_name === null) return -1;
        return new Intl.Collator("en").compare(a.team_name, b.team_name);
      });
    },

    teamHeaders() {
      var thisThis = this;
      let headers = [
        {
          text: "Team",
          value: "team_name",
          sortable: true,
          width: "1%",
          key: "data[].items[].dynalabelTitle"
        },
        {
          text: this.durationValHeaderText,
          value: "average_handle_time",
          align: "center",
          sortable: true,
          width: "1%",
          key: "data[].items[].metrics.averageDuration"
        },
        {
          text: "Calls/ Day",
          value: "average_call_volume",
          align: "center",
          sortable: true,
          width: "1%",
          key: "data[].items[].metrics.averageCallVolume"
        },
        {
          text: "Total Calls",
          value: "call_count",
          align: "center",
          sortable: true,
          width: "1%",
          key: "data[].items[].metrics.totalCalls"
        },
        {
          text: "Score",
          value: "average_call_score",
          align: "center",
          sortable: true,
          width: "1%",
          key: "data[].items[].metrics.callScoreAvg"
        }
      ];

      this.categorySkeletonData.forEach(function(category) {
        let nameFixed = thisThis.decodeHtml(category.category_title);
        headers.push({
          text: nameFixed,
          value: category.id,
          key: "data[].items[].metrics.categories[]",
          align: "center",
          sortable: true,
          width: "1%"
        });
      });
      return headers;
    },
    numColumns() {
      return DATA_COLUMN_LENGTH + this.categorySkeletonData.length;
    },
    getUnit() {
      return moment(this.end_date).diff(moment(this.start_date), "days") > 1
        ? "day"
        : "hour";
    },
    sortedItems() {
      return this.isDesc ? this.sortedRowData : this.groupedRowData;
    }
  },
  methods: {
    ...mapActions({
      addToDynaRequestCount: "scorecardsV2Aggregates/addToDynaRequestCount",
      setDynaLabelExpand: "scorecardsV2Aggregates/setDynaLabelExpand",
      pushDynaTeamReqId: "scorecardsV2Aggregates/pushDynaTeamReqId",
      setDynaLabelAggExpand: "scorecardsV2Aggregates/setDynaLabelAggExpand"
    }),
    sortIconClass(header) {
      let sortClass = "";

      if (
        this.myTooltipsSorted.includes(header.value) ||
        this.myTooltipsActive.includes(header.value)
      ) {
        sortClass = sortClass + " show-sort ";
      } else {
        sortClass = sortClass + " hide-sort ";
      }

      if (this.myTooltipsSorted.includes(header.value)) {
        sortClass = sortClass + " sortable-icon-dark ";
      } else {
        sortClass = sortClass + " sortable-icon-light ";
      }

      return sortClass;
    },
    teamHeaderClass(header) {
      return this.myTooltipsSorted.includes(header.value) ||
        this.myTooltipsActive.includes(header.value)
        ? "sortable-icon-dark"
        : "sortable-icon-light";
    },
    pushActiveTools(item) {
      this.myTooltipsActive.push(item);
    },
    removeActiveTools(item) {
      const index = this.myTooltipsActive.indexOf(item);
      if (index > -1) {
        this.myTooltipsActive.splice(index, 1);
      }
    },
    determineSortOrder() {
      switch (this.isDesc) {
        case 1:
          this.isDesc = 2;
          break;
        case 2:
          this.isDesc = null;
          break;
        default:
          this.isDesc = 1;
          break;
      }
    },
    sortColumn(index) {
      this.determineSortOrder();
      if (this.isDesc) {
        this.sortScItems(this.sortedRowData, index, this.isDesc == 2);
        if (!this.myTooltipsSorted.includes(index)) {
          this.myTooltipsSorted.push(index);
        }
      } else {
        const myIndex = this.myTooltipsSorted.indexOf(index);
        if (myIndex > -1) {
          this.myTooltipsSorted.splice(myIndex, 1);
        }
      }
    },
    teamDisabled(item) {
      return item.call_count < 1 && item.excluded_calls < 1 ? true : false;
    },
    loadDynalabelTeam(teamId) {
      let routeQuery = this.$route.query;
      routeQuery.team_id = teamId;

      this.$router
        .push({
          path:
            "/scorecards-v2/" +
            this.$route.params.scorecardId +
            "/dynalabels/" +
            this.dynalabelItem.id +
            "/agents/",
          query: routeQuery
        })
        .catch(err => {});
      setTimeout(function() {
        if (this.$vuetify) this.$vuetify.goTo(0);
      }, 1500);
    },
    decodeHtml(html) {
      var txt = document.createElement("textarea");
      txt.innerHTML = html;
      return txt.value;
    }
  },
  mounted() {},
  watch: {}
};
</script>

<style scoped>
::v-deep button.agent-btn span.v-btn__content {
  width: 200px;
  display: inline-block;
  text-overflow: ellipsis;
  overflow: hidden;
  text-align: left;
  padding-left: 3.5rem
}

.dyna-team-header {
  width: 1%;
  min-width: 1%;
  color: rgba(0, 0, 0, 0.54);
}

.dyna-team-header:hover {
  color: rgba(0, 0, 0, 0.87);
}

.show-sort {
  visibility: visible;
  opacity: 1;
  transition: opacity 100ms ease-in, visibility 0ms ease-in 0ms;
}

.hide-sort {
  visibility: hidden;
  opacity: 0;
  transition: opacity 100ms ease-in, visibility 0ms ease-in 250ms;
}

.sortable-icon-light {
  color: rgba(0, 0, 0, 0.54);
}

.sortable-icon-dark {
  color: rgba(0, 0, 0, 0.87);
}

::v-deep .tooltip-display-text {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -moz-box-orient: vertical;
  -ms-box-orient: vertical;
  box-orient: vertical;
  -webkit-line-clamp: 3;
  -moz-line-clamp: 3;
  -ms-line-clamp: 3;
  line-clamp: 3;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
